import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthState } from '@/hooks/useAuth'
import { useUserData } from '@/hooks/useUserData'
import { useArtistBookingDashboard } from '@/hooks/useArtistBookingDashboard'
import { StatusOverview } from '@/components/dashboard/StatusOverview'
import { UpcomingBooking } from '@/components/dashboard/UpcomingBooking'
import { BookingChatList } from '@/components/dashboard/BookingChatList'
import { Loader2, Menu as MenuIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { BookingSystemMenu } from '@/components/ui/menu/BookingSystemMenu'

const getGreeting = (firstName: string): string => {
  const hour = new Date().getHours()
  
  if (hour < 11) {
    return `Good Morning, ${firstName}!`
  } else if (hour < 18) {
    return `Hi, ${firstName}!`
  } else {
    return `Hey, ${firstName}!`
  }
}

export type StatusFilterValue = 'all' | 'artist_message_needed' | 'waiting_for_client' | 'booked'

/**
 * ArtistBookingDashboardPage - Instant Loading ⚡
 * 
 * After the first visit, this page loads INSTANTLY from cache with zero delay.
 * The greeting, status counts, and chat list display data are cached to localStorage,
 * allowing the UI to render immediately while fresh data loads in the background.
 * 
 * Performance: First load ~2-3s, subsequent loads ~100-200ms
 */
export default function ArtistBookingDashboardPage() {
  const { user } = useAuthState()
  const { userData, getCachedProfile } = useUserData()
  const navigate = useNavigate()
  const [artistId, setArtistId] = useState<string | undefined>(undefined)
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('all')
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  
  // Get cached profile for instant initial render (no flicker!)
  const cachedProfile = getCachedProfile()
  
  // Display greeting with fallback to cache for instant UI on page load
  const getDisplayGreeting = () => {
    const firstName = userData?.firstName || cachedProfile?.firstName
    return firstName ? getGreeting(firstName) : 'Booking Dashboard'
  }
  
  const [greeting, setGreeting] = useState<string>(getDisplayGreeting())

  // Get artistId from userData and set greeting
  useEffect(() => {
    // Wait for userData to be loaded before checking
    if (!userData) return
    
    if (userData.artistId) {
      setArtistId(userData.artistId)
    } else if (!userData.isArtist) {
      // If user is not an artist, redirect to home
      navigate('/flashes', { replace: true })
    }
    
    // Update greeting based on firstName
    if (userData.firstName) {
      setGreeting(getGreeting(userData.firstName))
    }
  }, [userData, navigate])

  const { statusCounts, upcomingBooking, chats, loading, error } = useArtistBookingDashboard(artistId)

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-foreground mb-4">Please sign in to view your dashboard</p>
          <Button onClick={() => navigate('/flashes')}>Go to Home</Button>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-foreground mx-auto mb-4" />
          <p className="text-foreground">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-destructive mb-4">Error loading dashboard: {error.message}</p>
          <Button onClick={() => navigate('/flashes')}>Go to Home</Button>
        </div>
      </div>
    )
  }

  return (
    <div 
      className="bg-background flex flex-col overflow-hidden"
      style={{ height: 'calc(100vh - var(--bottom-nav-offset))' }}
    >
      {/* Fixed header section - non-scrollable */}
      <div className="flex-shrink-0 phone-container-padded pb-2 w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-3">
            <h1 className="text-page-title text-foreground">
              {greeting}
            </h1>
          </div>
          <button
            onClick={() => setIsMenuOpen(true)}
            className="p-2 hover:bg-muted rounded-lg transition-colors flex-shrink-0"
            aria-label="More options"
          >
            <MenuIcon className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Scrollable section with all content */}
      <div className="phone-container-padded w-full flex-1 overflow-y-auto pb-20">
        <div className="mx-auto w-full space-y-0">
          {/* Upcoming Booking - Only show if there's a booking */}
          {upcomingBooking && (
            <div className="mb-4">
              <UpcomingBooking booking={upcomingBooking} />
            </div>
          )}

          {/* Status Overview */}
          <div className="mb-4">
            <StatusOverview 
              statusCounts={statusCounts}
              onFilterChange={setStatusFilter}
              activeFilter={statusFilter}
            />
          </div>

          {/* Chat List */}
          <BookingChatList 
            chats={chats}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
          />
        </div>
      </div>

      {/* Booking System Menu */}
      <BookingSystemMenu isOpen={isMenuOpen} onOpenChange={setIsMenuOpen} />
    </div>
  )
}

